import React from 'react';
import { useGetTradeDashboardQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import BarChart from '../../../components/charts/BarChart';
import PieChart from '../../../components/charts/PieChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxOpen,
  faNewspaper,
  faLeaf,
  faClock,
  faCheckCircle,
  faBan,
  faChessKnight,
  faCalendarDay,
  faUsers,
  faFire,
  faEye,
  faMousePointer,
  faShareAlt,
} from '@fortawesome/free-solid-svg-icons';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Theme palette: shades of the base green + neutral grays
const PIE_COLORS = [
  'rgba(57, 106, 84, 0.85)',
  'rgba(57, 106, 84, 0.55)',
  'rgba(57, 106, 84, 0.35)',
  'rgba(33, 37, 41, 0.75)',
  'rgba(33, 37, 41, 0.50)',
  'rgba(100, 116, 139, 0.75)',
  'rgba(100, 116, 139, 0.50)',
  'rgba(148, 163, 184, 0.75)',
];

const COUNTRY_COLORS = [
  'rgba(57, 106, 84, 0.80)',
  'rgba(57, 106, 84, 0.65)',
  'rgba(57, 106, 84, 0.50)',
  'rgba(57, 106, 84, 0.38)',
  'rgba(33, 37, 41, 0.70)',
  'rgba(33, 37, 41, 0.55)',
  'rgba(100, 116, 139, 0.70)',
  'rgba(100, 116, 139, 0.55)',
  'rgba(148, 163, 184, 0.70)',
  'rgba(148, 163, 184, 0.55)',
];

const getStatusBadge = (status: string) => {
  const map: Record<string, string> = {
    Active: 'badge bg-success',
    Pending: 'badge bg-warning text-dark',
    Blocked: 'badge bg-danger',
  };
  return map[status] || 'badge bg-secondary';
};

interface StatCardProps {
  label: string;
  value: number;
  icon: any;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon }) => (
  <div className="col-6 col-md-4 col-xl-3 mb-5">
    <div className="card card-flush bg-base">
      <div className="card-body d-flex align-items-center justify-content-between py-5 px-6">
        <div className="d-flex flex-column">
          <span className="fs-2hx fw-bold text-white lh-1 ls-n2">{value.toLocaleString()}</span>
          <span className="text-white opacity-75 pt-2 fw-semibold fs-6">{label}</span>
        </div>
        <FontAwesomeIcon icon={icon} className="text-white opacity-25" style={{ fontSize: 40 }} />
      </div>
    </div>
  </div>
);

const AdminTradeDashboard: React.FC = () => {
  const { data, isLoading, error } = useGetTradeDashboardQuery(undefined);

  if (isLoading) return <PageLoader />;

  if (error) {
    return (
      <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
        <div className="alert alert-danger m-5">Error loading dashboard</div>
      </div>
    );
  }

  const d = data?.data || {};

  const stats = [
    { label: 'Total Ads', value: d.total_ads || 0, icon: faNewspaper },
    { label: 'Total Users', value: d.total_users || 0, icon: faUsers },
    { label: 'Active Ads', value: d.active_ads || 0, icon: faCheckCircle },
    { label: 'Total Packages', value: d.total_packages || 0, icon: faBoxOpen },
    { label: 'Pending Ads', value: d.pending_ads || 0, icon: faClock },
    { label: 'Rejected Ads', value: d.rejected_ads || 0, icon: faBan },
    { label: 'Free Ads', value: d.free_ads || 0, icon: faLeaf },
    { label: 'Strategic Ads', value: d.strategic_ads || 0, icon: faChessKnight },
    { label: "Today's Ads", value: d.todays_ads || 0, icon: faCalendarDay },
  ];

  const countryData = d.country_wise_customers || [];
  const countryLabels = countryData.map((c: any) => c.country);
  const countryValues = countryData.map((c: any) => c.total);

  const catData = d.top_categories_by_ads || [];
  const catLabels = catData.map((c: any) => c.category);
  const catValues = catData.map((c: any) => c.total);

  const monthlyAds = d.monthly_ads || Array(12).fill(0);
  const trendingAds = d.top_trending_ads || [];

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                Trade Dashboard
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary">Home</span>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Trade Dashboard</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-fluid">

            {/* Stat Cards */}
            <div className="row g-5 g-xl-10 mb-5">
              {stats.map((s, i) => (
                <StatCard key={i} {...s} />
              ))}
            </div>

            {/* Charts row: Monthly Ads + Categories */}
            <div className="row g-5 g-xl-10 mb-5">

              {/* Monthly Ads Bar Chart */}
              <div className="col-12 col-xl-7">
                <div className="card card-flush h-100">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">Monthly Ads</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">
                        Ads published per month — {new Date().getFullYear()}
                      </span>
                    </h3>
                  </div>
                  <div className="card-body d-flex align-items-end p-0" style={{ minHeight: 300 }}>
                    <BarChart
                      labels={MONTH_LABELS}
                      datasets={[
                        {
                          label: 'Ads Published',
                          data: monthlyAds,
                          backgroundColor: 'rgba(57, 106, 84, 0.75)',
                          borderColor: 'rgba(57, 106, 84, 1)',
                        },
                      ]}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        scales: {
                          y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } },
                          x: { grid: { display: false } },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Top Categories Doughnut Chart */}
              <div className="col-12 col-xl-5">
                <div className="card card-flush h-100">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">Top Categories by Ads</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">Distribution of ads per category</span>
                    </h3>
                  </div>
                  <div
                    className="card-body d-flex align-items-center justify-content-center pt-0 pb-5"
                    style={{ minHeight: 280, position: 'relative' }}
                  >
                    {catLabels.length > 0 ? (
                      <div style={{ width: '100%', maxWidth: 320, height: 260 }}>
                        <PieChart
                          labels={catLabels}
                          data={catValues}
                          backgroundColor={PIE_COLORS}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: true,
                                position: 'bottom' as const,
                                labels: {
                                  boxWidth: 12,
                                  padding: 12,
                                  font: { size: 11 },
                                  usePointStyle: true,
                                },
                              },
                            },
                          }}
                        />
                      </div>
                    ) : (
                      <span className="text-muted">No category data available</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Country Wise Customers Bar Chart */}
            <div className="row g-5 g-xl-10 mb-5">
              <div className="col-12">
                <div className="card card-flush">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">Country Wise Customers</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">Users registered per country</span>
                    </h3>
                  </div>
                  <div className="card-body d-flex align-items-end p-0" style={{ minHeight: 300 }}>
                    {countryLabels.length > 0 ? (
                      <BarChart
                        labels={countryLabels}
                        datasets={[
                          {
                            label: 'Customers',
                            data: countryValues,
                            backgroundColor: countryLabels.map((_: any, i: number) => COUNTRY_COLORS[i % COUNTRY_COLORS.length]),
                            borderColor: 'transparent',
                          },
                        ]}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: {
                            y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } },
                            x: { grid: { display: false } },
                          },
                        }}
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center w-100 py-10">
                        <span className="text-muted">No country data available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Top Trending Ads Table */}
            <div className="row g-5 g-xl-10 mb-10">
              <div className="col-12">
                <div className="card card-flush">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faFire} style={{ color: '#396A54' }} />
                        Top Trending Ads
                      </span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">Highest performing ads by views</span>
                    </h3>
                  </div>
                  <div className="card-body pt-3">
                    {trendingAds.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4">
                          <thead>
                            <tr className="border-0">
                              <th className="p-0 w-30px">#</th>
                              <th className="p-0 min-w-200px">Ad Title</th>
                              <th className="p-0 min-w-120px">Category</th>
                              <th className="p-0 min-w-120px">Posted By</th>
                              <th className="p-0 min-w-80px">Status</th>
                              <th className="p-0 min-w-80px text-center">
                                <FontAwesomeIcon icon={faEye} className="me-1 text-muted" />Views
                              </th>
                              <th className="p-0 min-w-80px text-center">
                                <FontAwesomeIcon icon={faMousePointer} className="me-1 text-muted" />Clicks
                              </th>
                              <th className="p-0 min-w-80px text-center">
                                <FontAwesomeIcon icon={faShareAlt} className="me-1 text-muted" />Shares
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {trendingAds.map((ad: any, i: number) => (
                              <tr key={ad.id}>
                                <td>
                                  <span
                                    className="d-flex align-items-center justify-content-center text-white fw-bold fs-8"
                                    style={{
                                      width: 26,
                                      height: 26,
                                      borderRadius: 6,
                                      backgroundColor: i === 0 ? '#f6c000' : i === 1 ? '#a1a5b7' : i === 2 ? '#a47248' : '#396A54',
                                    }}
                                  >
                                    {i + 1}
                                  </span>
                                </td>
                                <td>
                                  <span className="text-dark fw-semibold fs-7">{ad.title || '-'}</span>
                                </td>
                                <td>
                                  <span className="text-muted fw-semibold fs-7">{ad.category}</span>
                                </td>
                                <td>
                                  <span className="text-muted fw-semibold fs-7">{ad.user}</span>
                                </td>
                                <td>
                                  <span className={getStatusBadge(ad.status)} style={{ fontSize: 11 }}>
                                    {ad.status}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <span className="fw-bold text-dark fs-7">{(ad.views || 0).toLocaleString()}</span>
                                </td>
                                <td className="text-center">
                                  <span className="fw-bold text-dark fs-7">{(ad.clicks || 0).toLocaleString()}</span>
                                </td>
                                <td className="text-center">
                                  <span className="fw-bold text-dark fs-7">{(ad.shares || 0).toLocaleString()}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="d-flex flex-column align-items-center justify-content-center py-10">
                        <FontAwesomeIcon icon={faFire} className="text-muted mb-3" style={{ fontSize: 36, opacity: 0.25 }} />
                        <span className="text-muted fs-6">No trending ads data available yet</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTradeDashboard;
